import { memo, useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import {
  BarChart,
  Bar,
  CartesianGrid,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  Cell,
} from 'recharts';
import { PIE_ALLOWED_COLORS } from '../../constants/gerencialesConstants';

/**
 * Renderiza los graficos de contratos archivados.
 * @param {Object} props - Props del componente.
 * @param {Object} props.chartData - Datos de gráficos desde el backend.
 * @param {Object|null} props.header - Header con metadata.
 * @returns {JSX.Element} Secciones de gráficos.
 */
const ArchivedContractsCharts = ({ chartData, header }) => {
  const byReason = Array.isArray(chartData?.by_reason) ? chartData.by_reason : [];
  const byDistrict = Array.isArray(chartData?.by_district) ? chartData.by_district : [];
  const byYear = Array.isArray(chartData?.by_year) ? chartData.by_year : [];

  const formatCount = (value) => {
    const numeric = Number(value ?? 0);
    if (Number.isNaN(numeric)) return '0';
    return numeric.toLocaleString('es-AR');
  };

  const reasonPieData = useMemo(
    () =>
      byReason.map((row, index) => ({
        name: row.label,
        value: Number(row.count ?? 0),
        fill: PIE_ALLOWED_COLORS[index % PIE_ALLOWED_COLORS.length],
      })),
    [byReason]
  );

  const districtBarData = useMemo(
    () =>
      byDistrict.map((row) => ({
        label: row.label,
        count: Number(row.count ?? 0),
      })),
    [byDistrict]
  );

  const yearBarData = useMemo(
    () =>
      byYear.map((row) => ({
        label: row.label,
        count: Number(row.count ?? 0),
      })),
    [byYear]
  );

  const totalContracts = header?.total_contracts ?? 0;

  const ChartCard = ({ title, chartId, children }) => (
    <Paper elevation={1} sx={{ mb: 1, mt: 1 }}>
      <Box
        sx={{
          backgroundColor: '#3b3f46',
          color: 'white',
          px: 2.5,
          py: 1.5,
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
          fontSize: 30,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {title}
        </Typography>
      </Box>
      <Box id={chartId} sx={{ p: 2 }}>
        {children}
      </Box>
    </Paper>
  );

  return (
    <>
      <ChartCard
        title={`Contratos dados de baja por motivo (Total: ${totalContracts})`}
        chartId="gerenciales-archived-by-reason"
      >
        <Box sx={{ height: { xs: 280, md: 320 } }}>
          {reasonPieData.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={reasonPieData}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={110}
                  isAnimationActive={false}
                  label={({ name, value }) => `${name}: ${formatCount(value)}`}
                >
                  {reasonPieData.map((entry, index) => (
                    <Cell key={`cell-${entry.name}-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <RechartsTooltip formatter={(value) => formatCount(value)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Sin datos para graficar
            </Typography>
          )}
        </Box>
      </ChartCard>

      <ChartCard
        title="Contratos dados de baja por distrito"
        chartId="gerenciales-archived-by-district"
      >
        <Box sx={{ height: { xs: 280, md: 340 } }}>
          {districtBarData.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={districtBarData}
                layout="vertical"
                margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={(value) => formatCount(value)} />
                <YAxis type="category" dataKey="label" width={160} />
                <RechartsTooltip formatter={(value) => formatCount(value)} />
                <Legend />
                <Bar
                  dataKey="count"
                  name="Contratos"
                  fill={PIE_ALLOWED_COLORS[1]}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Sin datos para graficar
            </Typography>
          )}
        </Box>
      </ChartCard>

      <ChartCard
        title="Contratos dados de baja por año"
        chartId="gerenciales-archived-by-year"
      >
        <Box sx={{ height: { xs: 280, md: 340 } }}>
          {yearBarData.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={yearBarData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" />
                <YAxis tickFormatter={(value) => formatCount(value)} />
                <RechartsTooltip formatter={(value) => formatCount(value)} />
                <Legend />
                <Bar
                  dataKey="count"
                  name="Contratos"
                  fill={PIE_ALLOWED_COLORS[2]}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Sin datos para graficar
            </Typography>
          )}
        </Box>
      </ChartCard>
    </>
  );
};

export default memo(ArchivedContractsCharts);
